"use client";

import React, { useState, useMemo, useEffect } from "react";
import { X, Check, AlertCircle } from "lucide-react";
import Select from "react-select";
import { SUPPORTED_COUNTRIES } from "@/lib/flutterwave_v4/countries";
import { useLanguage } from "@/contexts/LanguageContext";
import { getStrings } from "@/i18n/dictionaries";
import { FLUTTERWAVE_COUNTRIES } from "@/config/paymentRegions";
import {
  getCountryPaymentConfig,
  hasMultipleMethods,
  getMethodDisplay,
  PAYMENT_METHOD,
  type PaymentMethodId,
} from "@/lib/flutterwave_v4/payment_config";

interface PaymentMethodModalProps {
  planId: string;
  planName: string;
  duration: string;
  onClose: () => void;
  onContinue: (
    gateway: "stripe" | "flutterwave",
    selectedCountry?: { code: string; name: string },
    flutterwaveMethod?: "momo" | "bank_transfer"
  ) => void;
}

// Custom styles for react-select to match our design
const customSelectStyles = {
  control: (provided: Record<string, any>, state: { isFocused: boolean }) => ({
    ...provided,
    borderRadius: "0.375rem",
    borderColor: state.isFocused ? "#f97316" : "#cbd5e1",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(249, 115, 22, 0.2)" : "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    padding: "0.125rem 0.5rem",
    fontSize: "0.875rem",
    "&:hover": {
      borderColor: state.isFocused ? "#f97316" : "#cbd5e1",
    },
  }),
  option: (provided: Record<string, any>, state: { isSelected: boolean; isFocused: boolean }) => ({
    ...provided,
    fontSize: "0.875rem",
    backgroundColor: state.isSelected
      ? "#f97316"
      : state.isFocused
      ? "#fed7aa"
      : "white",
    color: state.isSelected ? "white" : "#1e293b",
    "&:hover": {
      backgroundColor: state.isSelected ? "#f97316" : "#fed7aa",
    },
  }),
  placeholder: (provided: Record<string, any>) => ({
    ...provided,
    fontSize: "0.875rem",
    color: "#94a3b8",
  }),
  singleValue: (provided: Record<string, any>) => ({
    ...provided,
    fontSize: "0.875rem",
    color: "#1e293b",
  }),
};

export default function PaymentMethodModal({
  planId,
  planName,
  duration,
  onClose,
  onContinue,
}: PaymentMethodModalProps) {
  const { locale } = useLanguage();
  const t = useMemo(() => getStrings(locale, "payment"), [locale]);
  const [selectedTab, setSelectedTab] = useState<"stripe" | "flutterwave">("stripe");
  const [recommendedTab, setRecommendedTab] = useState<"stripe" | "flutterwave">("stripe");
  const [selectedCountry, setSelectedCountry] = useState<string>("");
  const [flutterwaveMethod, setFlutterwaveMethod] = useState<"momo" | "bank_transfer" | "opay">("momo");

  // Auto-detect user country via IP geolocation and set default tab
  useEffect(() => {
    const detectCountry = async () => {
      try {
        const res = await fetch("/api/location");
        const data = await res.json();
        const country: string = data.country;

        if (FLUTTERWAVE_COUNTRIES.includes(country)) {
          setSelectedTab("flutterwave");
          setRecommendedTab("flutterwave");
        } else {
          setSelectedTab("stripe");
          setRecommendedTab("stripe");
        }
      } catch (error) {
        console.error("Location detection failed:", error);
        setSelectedTab("stripe");
        setRecommendedTab("stripe");
      }
    };

    detectCountry();
  }, []);

  // Derive config for the selected country
  const countryConfig = useMemo(
    () => (selectedCountry ? getCountryPaymentConfig(selectedCountry) : null),
    [selectedCountry],
  );
  const showMethodSelector = countryConfig ? countryConfig.methods.length > 1 : false;

  // Transform countries for react-select
  const countryOptions = SUPPORTED_COUNTRIES.map((country) => ({
    value: country.code,
    label: country.name,
  }));

  const handleContinue = async () => {
    if (selectedTab === "stripe") {
      onContinue("stripe");
    } else {
      // For Flutterwave, ensure a country is selected
      if (!selectedCountry) {
        return; // Button should be disabled, but double-check
      }
      const country = SUPPORTED_COUNTRIES.find(c => c.code === selectedCountry);
      if (country) {
        // Save selected country to user profile before continuing
        try {
          const response = await fetch("/api/user/profile", {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              currentCountry: country.code,
            }),
          });
          
          if (!response.ok) {
            const errorText = await response.text().catch(() => "Unknown error");
            console.error(
              `[PaymentMethodModal] Failed to update country (HTTP ${response.status}): ${errorText}. Payment may fail if country is required.`
            );
            // Still continue - the backend will provide a proper error if country is missing
          }
        } catch (error) {
          console.error("[PaymentMethodModal] Failed to update country (network error):", error);
          // Continue anyway - the backend will provide a proper error if country is missing
        }
        onContinue("flutterwave", country, showMethodSelector ? (flutterwaveMethod as "momo" | "bank_transfer") : undefined);
      }
    }
  };

  const isContinueEnabled = selectedTab === "stripe" || (selectedTab === "flutterwave" && selectedCountry);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="relative w-full max-w-2xl max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-xl bg-white shadow-2xl">
        {/* Header */}
        <div className="border-b border-slate-200 px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-900">{t.choose_method}</h2>
              <p className="text-sm text-slate-600">
                {planName} Plan - {duration}
              </p>
            </div>
            <button
              onClick={onClose}
              className="rounded-full p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200">
          <div className="flex flex-nowrap gap-0 overflow-x-auto overflow-y-hidden whitespace-nowrap [-webkit-overflow-scrolling:touch] scrollbar-hide">
            <button
              onClick={() => setSelectedTab("flutterwave")}
              className={`shrink-0 flex-none px-6 py-3 text-sm font-semibold transition ${
                selectedTab === "flutterwave"
                  ? "border-b-2 border-orange-500 text-orange-600"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <span className="inline-flex items-center justify-center gap-2 whitespace-nowrap">
                {t.flutterwave_tab}
                {recommendedTab === "flutterwave" && (
                  <span className="inline-flex items-center rounded-full bg-emerald-500 px-2.5 py-0.5 text-xs font-bold text-white">
                    {t.recommended_region}
                  </span>
                )}
              </span>
            </button>
            <button
              onClick={() => setSelectedTab("stripe")}
              className={`shrink-0 flex-none px-6 py-3 text-sm font-semibold transition ${
                selectedTab === "stripe"
                  ? "border-b-2 border-indigo-600 text-indigo-600"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <span className="inline-flex items-center justify-center gap-2 whitespace-nowrap">
                {t.stripe_tab}
                {recommendedTab === "stripe" && (
                  <span className="inline-flex items-center rounded-full bg-emerald-500 px-2.5 py-0.5 text-xs font-bold text-white">
                    {t.recommended_cheapest}
                  </span>
                )}
              </span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 sm:px-6 py-4">
          {selectedTab === "flutterwave" ? (
            <div className="space-y-2">
              <div className="rounded-lg bg-orange-50 p-3">
                <h3 className="font-semibold text-orange-900">
                  {t.africa_friendly}
                </h3>
                <p className="mt-0.5 text-sm text-orange-800">
                  {t.africa_friendly_desc}
                </p>
              </div>

              <div className="flex items-start gap-3 rounded-lg bg-amber-50 p-3">
                <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
                <div>
                  <p className="text-sm text-amber-800">
                    {t.processing_fee}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 rounded-lg bg-slate-50 p-3">
                <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-slate-500" />
                <div>
                  <p className="text-sm text-slate-600">
                    {t.country_not_supported_1}{" "}
                    <button
                      type="button"
                      onClick={() => setSelectedTab("stripe")}
                      className="inline font-semibold text-indigo-600 underline hover:text-indigo-500 transition"
                    >
                      {t.stripe_tab}
                    </button>{" "}
                    {t.country_not_supported_2}
                  </p>
                </div>
              </div>

              <div className="mt-2">
                <label htmlFor="country-select" className="mb-1 block text-sm font-semibold text-slate-700">
                  {t.select_country}
                </label>
                <Select
                  inputId="country-select"
                  options={countryOptions}
                  value={countryOptions.find((option) => option.value === selectedCountry) || null}
                  onChange={(option) => {
                    const nextCountry = option?.value || "";
                    setSelectedCountry(nextCountry);
                    // Set default method from config
                    const cfg = nextCountry ? getCountryPaymentConfig(nextCountry) : null;
                    if (cfg) {
                      setFlutterwaveMethod(cfg.defaultMethod as "momo" | "bank_transfer" | "opay");
                    }
                  }}
                  placeholder={t.search_country}
                  isClearable
                  isSearchable
                  styles={customSelectStyles}
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
                <p className="mt-1 text-xs text-slate-500">
                  {t.select_country_desc}
                </p>
              </div>

              {showMethodSelector && countryConfig && (
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <h4 className="text-sm font-semibold text-slate-900">
                    {countryConfig.name} {t.payment_option}
                  </h4>
                  <p className="mt-1 text-xs text-slate-600">
                    {t.choose_how_to_pay}
                  </p>

                  <div className="mt-3 grid gap-2">
                    {countryConfig.methods.map((method) => {
                      const display = getMethodDisplay(method);
                      return (
                        <label
                          key={method}
                          className="flex cursor-pointer items-start gap-3 rounded-md border border-slate-200 p-3 hover:bg-slate-50"
                        >
                          <input
                            type="radio"
                            name="flw-method"
                            value={method}
                            checked={flutterwaveMethod === method}
                            onChange={() =>
                              setFlutterwaveMethod(
                                method as "momo" | "bank_transfer" | "opay",
                              )
                            }
                            className="mt-1"
                          />
                          <div>
                            <div className="text-sm font-semibold text-slate-900">
                              {display.emoji} {display.label}
                            </div>
                            <div className="text-xs text-slate-600">
                              {display.description}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-start gap-3 rounded-lg bg-indigo-50 p-3">
                <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-indigo-600" />
                <div>
                  <h3 className="font-semibold text-indigo-900">{t.global_support}</h3>
                  <p className="mt-0.5 text-sm text-indigo-800">
                    {t.global_support_desc}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 rounded-lg bg-indigo-50 p-3">
                <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-indigo-600" />
                <div>
                  <h3 className="font-semibold text-indigo-900">{t.instant_activation}</h3>
                  <p className="mt-0.5 text-sm text-indigo-800">
                    {t.instant_activation_desc}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 rounded-lg bg-indigo-50 p-3">
                <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-indigo-600" />
                <div>
                  <h3 className="font-semibold text-indigo-900">{t.bank_security}</h3>
                  <p className="mt-0.5 text-sm text-indigo-800">
                    {t.bank_security_desc}
                  </p>
                  <p className="mt-2 text-xs text-indigo-600">
                    {t.learn_more}{" "}
                    <a
                      href="https://docs.stripe.com/security"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:text-indigo-500 transition"
                    >
                      {t.stripe_security_doc}
                    </a>
                  </p>
                </div>
              </div>

              <p className="text-center text-xs text-slate-400">
                {t.trusted_worldwide}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between gap-3">
            <button
              onClick={onClose}
              className="rounded-md border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              {t.cancel}
            </button>
            <button
              onClick={handleContinue}
              disabled={!isContinueEnabled}
              className={`rounded-md px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition ${
                isContinueEnabled
                  ? selectedTab === "flutterwave"
                    ? "bg-orange-600 hover:bg-orange-500"
                    : "bg-indigo-600 hover:bg-indigo-500"
                  : "cursor-not-allowed bg-slate-300"
              }`}
            >
              {t.continue_btn}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
